<template>
  <Dialog v-model:visible="visible" header="Product Workflow" :style="{ width: '50rem' }" modal>
    <div v-if="loading" class="text-surface-400 text-sm py-4">Loading...</div>
    <div v-else-if="workflow">
      <div v-if="isEmpty" class="text-surface-500 text-sm py-4">No tasks for this product.</div>

      <!-- Product-level plans -->
      <section v-if="workflow.productPlans.length > 0" class="mb-6">
        <h3 class="text-sm font-medium text-surface-300 mb-3">Product Tasks</h3>
        <TaskPlanCard
          v-for="plan in workflow.productPlans"
          :key="plan.id"
          :plan="plan"
          @rerun-complete="fetchWorkflow"
        />
      </section>

      <!-- Image-level plans -->
      <section v-for="image in workflow.images" :key="image.imageId" class="mb-6">
        <h3 class="text-sm font-medium text-surface-300 mb-3">
          Image {{ image.imageId.slice(0, 8) }}...
        </h3>
        <div v-for="version in image.versions" :key="version.id">
          <div v-if="version.plans.length > 0">
            <div class="text-xs text-surface-500 mb-1 ml-1">
              {{ version.original ? 'Original' : 'Output' }}
              <span v-if="version.created">
                &middot; {{ formatDate(version.created) }}
              </span>
            </div>
            <TaskPlanCard
              v-for="plan in version.plans"
              :key="plan.id"
              :plan="plan"
              @rerun-complete="fetchWorkflow"
            />
          </div>
        </div>
      </section>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import Dialog from 'primevue/dialog'
import dayjs from 'dayjs'
import type { ProductWorkflowDto } from '@/api/generated'
import { useApiClient } from '@/api/apiClient'
import TaskPlanCard from './TaskPlanCard.vue'

const props = defineProps<{
  productId: string
}>()

const visible = defineModel<boolean>('visible', { default: false })

const { api } = useApiClient()

const loading = ref(false)
const workflow = ref<ProductWorkflowDto | null>(null)

const isEmpty = computed(
  () =>
    workflow.value &&
    workflow.value.productPlans.length === 0 &&
    workflow.value.images.length === 0,
)

async function fetchWorkflow() {
  loading.value = true
  try {
    const { data } = await api.taskPlanControllerGetProductWorkflow({
      path: { productId: props.productId },
    })
    workflow.value = data ?? null
  } finally {
    loading.value = false
  }
}

watch(visible, newVal => {
  if (newVal) {
    fetchWorkflow()
  }
}, { immediate: true })

function formatDate(epoch: number): string {
  return dayjs(epoch).format('YYYY-MM-DD HH:mm')
}
</script>
